import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getChemicals, createChemicalChange, getChemicalDashboard } from '../services/chemicalService';
import { useLocationContext } from '../contexts/LocationContext';
import { track } from '../services/analyticsService';

const LogChemicalChange = () => {
  const { currentLocation } = useLocationContext();
  const navigate = useNavigate();

  const [chemicals, setChemicals] = useState({ ss: [], auto: [] });
  const [selectedChemicalId, setSelectedChemicalId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [bottles, setBottles] = useState(1);
  const [notes, setNotes] = useState('');
  const [frequentChemicals, setFrequentChemicals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentLocation?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [chemData, dashData] = await Promise.all([
        getChemicals(),
        getChemicalDashboard()
      ]);

      const ss = chemData.chemicals.filter(c => c.location === 'SS');
      const auto = chemData.chemicals.filter(c => c.location === 'Auto');
      setChemicals({ ss, auto });

      // Find most frequently changed chemicals (those with the most changes)
      const allStats = [...dashData.ss_chemicals, ...dashData.auto_chemicals];
      const frequent = allStats
        .filter(s => s.total_changes > 0)
        .sort((a, b) => b.total_changes - a.total_changes)
        .slice(0, 6)
        .map(s => s.chemical);
      setFrequentChemicals(frequent);
    } catch (err) {
      setError('Failed to load chemicals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedChemicalId) {
      setError('Please select a chemical');
      return;
    }

    try {
      setSaving(true);
      const count = Math.max(1, parseInt(bottles, 10) || 1);
      await createChemicalChange(selectedChemicalId, { date, notes, bottles: count });
      track('soap_change_log');
      setSuccess(
        count > 1
          ? `Logged ${count} bottles loaded.`
          : 'Soap change logged successfully!'
      );
      setNotes('');
      setBottles(1);
      // Refresh frequent chemicals
      fetchData();
    } catch (err) {
      setError('Failed to log change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const quickLog = async (chemicalId) => {
    setError('');
    setSuccess('');
    try {
      setSaving(true);
      await createChemicalChange(chemicalId, {
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      const chem = [...chemicals.ss, ...chemicals.auto].find(c => c.id === chemicalId);
      setSuccess(`Logged soap change for ${chem?.name || 'chemical'}!`);
      fetchData();
    } catch (err) {
      setError('Failed to log change.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Log Soap Change</h1>
        <button
          onClick={() => navigate('/inventory')}
          className="text-gray-600 hover:text-gray-800 text-sm font-medium"
        >
          Back to Inventory
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Quick Log Buttons */}
      {frequentChemicals.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick Log (Today)</h2>
          <div className="flex flex-wrap gap-2">
            {frequentChemicals.map(chem => (
              <button
                key={chem.id}
                onClick={() => quickLog(chem.id)}
                disabled={saving}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {chem.name}
                <span className="ml-1 text-xs text-blue-400">({chem.location})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 overflow-hidden max-w-full">
        <h2 className="text-lg font-semibold text-gray-900">Log with Details</h2>

        {/* Chemical Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Chemical</label>
          <select
            value={selectedChemicalId}
            onChange={(e) => setSelectedChemicalId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a chemical...</option>
            {chemicals.ss.length > 0 && (
              <optgroup label="SS Bays">
                {chemicals.ss.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </optgroup>
            )}
            {chemicals.auto.length > 0 && (
              <optgroup label="Auto Wash">
                {chemicals.auto.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{ WebkitAppearance: 'none', maxWidth: '100%', boxSizing: 'border-box', minHeight: '42px' }}
          />
          {date > new Date().toISOString().split('T')[0] && (
            <div className="mt-2 bg-yellow-50 border border-yellow-200 text-yellow-700 px-3 py-2 rounded text-sm">
              This date is in the future. Are you sure?
            </div>
          )}
        </div>

        {/* Bottles loaded */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bottles loaded</label>
          <input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={bottles}
            onChange={(e) => setBottles(e.target.value)}
            onBlur={() => setBottles(Math.max(1, parseInt(bottles, 10) || 1))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{ minHeight: '42px' }}
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave at 1 for a normal jug swap. Bump it up when a holding tank takes several at once.
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. Noticed lower than usual, checked for leaks"
          />
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Logging...' : 'Log Change'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/inventory')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default LogChemicalChange;
